import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Stock, Order } from '../../types';
import { StockCard } from '../molecules/StockCard';
import { OrderForm } from '../molecules/OrderForm';
import { Modal } from '../atoms/Modal';
import { MockTradingAPI } from '../../services/mockAPI';

export const StockWatchlist: React.FC = () => {
  const [selectedStock, setSelectedStock] = useState<Stock | null>(null);
  const [orderType, setOrderType] = useState<'buy' | 'sell' | null>(null);
  const queryClient = useQueryClient();

  const { data: stocks } = useQuery<Stock[]>({
    queryKey: ['stocks'],
    queryFn: MockTradingAPI.getStocks,
    refetchInterval: 5000,
  });

  const orderMutation = useMutation<Order, Error, Omit<Order, 'id' | 'status' | 'timestamp'>>({
    mutationFn: MockTradingAPI.placeOrder,
    onSuccess: (order) => {
      const orders = JSON.parse(localStorage.getItem('mock-orders') || '[]') as Order[];
      orders.push(order);
      localStorage.setItem('mock-orders', JSON.stringify(orders));
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      setSelectedStock(null);
      setOrderType(null);
    },
  });

  const handleOrder = (stock: Stock, type: 'buy' | 'sell') => {
    setSelectedStock(stock);
    setOrderType(type);
  };

  const submitOrder = (orderData: Omit<Order, 'id' | 'status' | 'timestamp'>) => {
    orderMutation.mutate(orderData);
  };

  return (
    <div data-testid="stock-watchlist">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Market Watch</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {stocks?.map(stock => (
          <StockCard
            key={stock.symbol}
            stock={stock}
            onBuy={(stock) => handleOrder(stock, 'buy')}
            onSell={(stock) => handleOrder(stock, 'sell')}
          />
        ))}
      </div>

      <Modal
        isOpen={!!selectedStock}
        onClose={() => {
          setSelectedStock(null);
          setOrderType(null);
        }}
        title="Place Order"
      >
        {selectedStock && (
          <OrderForm
            stock={selectedStock}
            type={orderType!}
            onSubmit={submitOrder}
            onCancel={() => {
              setSelectedStock(null);
              setOrderType(null);
            }}
          />
        )}
      </Modal>
    </div>
  );
};